import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Row, FormGroup, Label, Spinner, Badge, Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import CountUp from "react-countup";
import { fetchFarmStatistics } from "../../slices/dashboard/thunk";
import { fetchFarmhouses } from "../../slices/farmhouse/thunk";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import bootstrapPlugin from "@fullcalendar/bootstrap";

// CountUp Component
const CountUpComponent = ({ end }) => {
  return <CountUp start={0} end={end || 0} duration={2} separator="," />;
};

// Statistics Widget
const StatWidget = ({ label, value, icon, color = "primary", isCurrency = false }) => {
  return (
    <Col xl={3} md={6}>
      <Card>
        <CardBody style={{ height: "100px", overflow: "hidden" }}>
          <div className="d-flex align-items-center counter-widget">
            <div className="flex-grow-1">
              <p className="text-uppercase fw-semibold fs-14 text-muted mb-1">
                {label}
              </p>
              <h2 className="mb-0">
                {isCurrency ? (
                  <span>₹<CountUpComponent end={value || 0} /></span>
                ) : (
                  <CountUpComponent end={value || 0} />
                )}
              </h2>
            </div>
            <div className="avatar-sm flex-shrink-0">
              <span className={`avatar-title text-${color} rounded-circle fs-3`} style={{ backgroundColor: '#f5f5f5' }}>
                <i className={icon}></i>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

const Statement = () => {
  const dispatch = useDispatch();
  const [selectedFarm, setSelectedFarm] = useState(null);
  const [eventModal, setEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const toggleEventModal = () => setEventModal(!eventModal);

  const { farmStatistics } = useSelector((state) => ({
    farmStatistics: state.Dashboard.farmStatistics,
  }));

  const { farmhouses, loading: farmsLoading } = useSelector((state) => ({
    farmhouses: state.Farmhouse.farmhouses || [],
    loading: state.Farmhouse.loading,
  }));

  // Fetch farmhouses on component mount
  useEffect(() => {
    if (farmhouses.length === 0) {
      dispatch(fetchFarmhouses({ limit: 1000 })); // Fetch all farmhouses
    }
  }, [dispatch, farmhouses.length]);

  // Fetch statistics when farm is selected
  useEffect(() => {
    if (selectedFarm) {
      dispatch(fetchFarmStatistics(selectedFarm.value));
    }
  }, [dispatch, selectedFarm]);



  // Prepare farm options for dropdown with A to Z sorting
  const farmOptions = farmhouses
    .map((farm) => ({
      value: farm.id,
      label: `${farm.name}${farm.farmNo ? ` ${farm.farmNo}` : ""}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) { return dateString; }
  };

  // Get date part YYYY-MM-DD without UTC shift (date-only strings are parsed as UTC midnight and toISOString() can change the date in IST)
  const getDatePart = (dateVal) => {
    if (!dateVal) return '';
    const d = dateVal;
    if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) return d.substring(0, 10);
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // Build check-in/check-out display using date + time fields to avoid date-only UTC midnight (e.g. 5:30 AM IST)
  const getCheckInDisplay = (booking) => {
    if (!booking) return "N/A";
    const dateStr = getDatePart(booking.bookingDate);
    const timeStr = (booking.bookingTimeFrom || '').trim();
    if (dateStr && timeStr) {
      const combined = `${dateStr}T${timeStr}`;
      return formatDate(combined);
    }
    return formatDate(booking.checkInTime || booking.checkIn || booking.bookingDate);
  };

  const getCheckOutDisplay = (booking) => {
    if (!booking) return "N/A";
    const dateStr = getDatePart(booking.bookingEndDate || booking.bookingDate);
    const timeStr = (booking.bookingTimeTo || '').trim();
    if (dateStr && timeStr) {
      const combined = `${dateStr}T${timeStr}`;
      return formatDate(combined);
    }
    return formatDate(booking.checkOutTime || booking.checkOut || booking.bookingEndDate || booking.bookingDate);
  };

  const getBookingTypeLabel = (type) => {
    if (!type) return 'Regular';
    return type.includes('WEEKEND') ? 'Weekend' : 'Regular';
  };

  const getBookingDurationDisplay = (booking) => {
    const hours = booking.bookingHours || 0;
    return `${hours} Hours`;
  };

  const statistics = farmStatistics.data;

  // Flatten bookings if they are grouped by date (API response handling)
  const bookings = React.useMemo(() => {
    const rawBookings = statistics?.bookings || [];
    let allBookings = [];

    if (rawBookings.length > 0 && rawBookings[0].bookings && Array.isArray(rawBookings[0].bookings)) {
      allBookings = rawBookings.flatMap(group => group.bookings || []);
    } else {
      allBookings = rawBookings;
    }

    // Deduplicate by ID to prevent multiple events for the same booking
    const uniqueBookings = [];
    const seenIds = new Set();

    allBookings.forEach(booking => {
      if (booking && booking.id && !seenIds.has(booking.id)) {
        seenIds.add(booking.id);
        uniqueBookings.push(booking);
      }
    });

    return uniqueBookings;
  }, [statistics]);

  // Map bookings to FullCalendar events
  const calendarEvents = bookings.map(booking => {
    let backgroundColor = '#f3f6f9';
    let borderColor = '#f3f6f9';
    let textColor = '#212529';

    // Prioritize Payment Status
    if (booking.paymentStatus === 'paid') {
      backgroundColor = 'rgba(22, 163, 74, 0.1)'; // Green - Low opacity (approx 10%)
      borderColor = 'rgba(22, 163, 74, 0.2)';
      textColor = '#16a34a';
    } else if (booking.paymentStatus === 'partial') {
      backgroundColor = 'rgba(245, 158, 11, 0.1)'; // Amber - same style as Paid/Incomplete
      borderColor = 'rgba(245, 158, 11, 0.2)';
      textColor = '#d97706';
    } else if (booking.paymentStatus === 'incomplete') {
      backgroundColor = 'rgba(220, 38, 38, 0.1)'; // Red - Low opacity
      borderColor = 'rgba(220, 38, 38, 0.2)';
      textColor = '#dc2626';
    } else if (booking.paymentStatus === 'cancel' || booking.paymentStatus === 'cancelled') {
      backgroundColor = '#dc2626'; // Dark Red - Solid
      borderColor = '#dc2626';
      textColor = '#ffffff';
    }

    const durationLabel = getBookingDurationDisplay(booking);

    // Attempt to find the customer name from various possible fields
    const customerName = booking.userInfo?.name || booking.user?.name || booking.userName || booking.customerName || 'Guest';

    // Title Format for fallback: Name - Duration
    const eventTitle = `${customerName} - ${durationLabel}`;

    const formatTime = (timeStr) => {
      if (!timeStr) return '';
      try {
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
      } catch (e) { return timeStr; }
    };

    let timeRange = '';

    const formatDateShort = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    if (booking.bookingTimeFrom && booking.bookingTimeTo) {
      const startTime = formatTime(booking.bookingTimeFrom);
      const endTime = formatTime(booking.bookingTimeTo);

      const startDate = booking.bookingDate ? new Date(booking.bookingDate).toISOString().split('T')[0] : '';
      const endDate = booking.bookingEndDate ? new Date(booking.bookingEndDate).toISOString().split('T')[0] : '';

      if (startDate && endDate && startDate !== endDate) {
        timeRange = `${formatDateShort(startDate)}, ${startTime} - ${formatDateShort(endDate)}, ${endTime}`;
      } else {
        timeRange = startTime === endTime ? startTime : `${startTime} - ${endTime}`;
      }
    } else if ((booking.checkInTime || booking.checkIn) && (booking.checkOutTime || booking.checkOut)) {
      try {
        const start = new Date(booking.checkInTime || booking.checkIn);
        const end = new Date(booking.checkOutTime || booking.checkOut);

        const isSameDay = start.toDateString() === end.toDateString();

        if (!isSameDay) {
          const startStr = `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
          const endStr = `${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
          timeRange = `${startStr} - ${endStr}`;
        } else {
          timeRange = `${start.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} - ${end.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
        }
      } catch (e) { }
    }

    return {
      id: booking.id.toString(),
      title: eventTitle,
      start: booking.bookingDate || booking.checkInTime,
      end: booking.bookingEndDate || booking.checkOutTime || booking.bookingDate,
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      textColor: textColor,
      extendedProps: { ...booking, durationLabel, customerName, timeRange }
    };
  });

  const renderEventContent = (eventInfo) => {
    const { extendedProps, backgroundColor, borderColor, textColor } = eventInfo.event;

    // Determine status color logic again for consistent styling if needed, 
    // or rely on the passed event colors. 
    // We'll use a cleaner "chip" style.

    return (
      <div
        className="fc-content-wrapper"
        style={{
          borderLeft: `3px solid ${textColor}`,
          backgroundColor: backgroundColor,
          padding: '4px 8px',
          borderRadius: '4px',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div className="d-flex align-items-center justify-content-between">
          <span
            className="fw-bold text-truncate"
            style={{ fontSize: '0.85rem', color: textColor, maxWidth: '100%' }}
            title={extendedProps.customerName}
          >
            {extendedProps.customerName}
          </span>
        </div>

        {/* Added Time Range Display */}
        {extendedProps.timeRange && (
          <div className="d-flex align-items-center mt-1">
            <i className="las la-clock me-1" style={{ fontSize: '0.8rem', color: textColor, opacity: 0.8 }}></i>
            <span className="text-truncate fw-medium" style={{ fontSize: '0.75rem', color: textColor, opacity: 0.9 }}>
              {extendedProps.timeRange}
            </span>
          </div>
        )}

        <div className="d-flex align-items-center mt-1">
          <i className="las la-hourglass-half me-1" style={{ fontSize: '0.8rem', color: textColor, opacity: 0.8 }}></i>
          <span className="text-truncate" style={{ fontSize: '0.75rem', color: textColor, opacity: 0.9 }}>
            {extendedProps.durationLabel}
          </span>
        </div>
      </div>
    );
  };

  const handleEventClick = (info) => {
    setSelectedEvent(info.event.extendedProps);
    setEventModal(true);
  };

  // Calculate current and upcoming bookings
  const currentBookings = bookings.filter(b => b.bookingStatus === 'current');
  const upcomingBookings = bookings.filter(b => b.bookingStatus === 'upcoming');

  return (
    <div className="statement-page">
      <Row className="">
        <Col md={12}>
          <Card className="border-0 shadow-sm">
            <CardBody>
              <FormGroup className="mb-0">
                <Label for="farmSelect" className="fw-medium mb-2 text-muted text-uppercase fs-12">
                  Select Farmhouse
                </Label>
                <Select
                  id="farmSelect"
                  value={selectedFarm}
                  onChange={setSelectedFarm}
                  options={farmOptions}
                  placeholder="Select a farmhouse..."
                  isSearchable
                  isLoading={farmsLoading}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: '8px',
                      border: '1px solid #e9ecef',
                      boxShadow: 'none',
                      '&:hover': { border: '1px solid #ced4da' }
                    })
                  }}
                />
              </FormGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {farmStatistics.loading && (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2 text-muted">Loading farm statistics...</p>
        </div>
      )}

      {farmStatistics.error && (
        <div className="alert alert-danger shadow-sm border-0" role="alert">
          <i className="ri-error-warning-line me-2"></i>
          {farmStatistics.error}
        </div>
      )}

      {statistics && !farmStatistics.loading && (
        <>
          {/* Statistics Cards */}
          <Row>
            <StatWidget
              label="Total Orders"
              value={statistics.totalOrders || 0}
              icon="las la-shopping-cart"
              color="primary"
            />
            <StatWidget
              label="Total Income"
              value={statistics.totalIncome || 0}
              icon="las la-rupee-sign"
              color="success"
              isCurrency={true}
            />
            <StatWidget
              label="Paid Orders"
              value={statistics.paidOrders || 0}
              icon="las la-check-circle"
              color="success"
            />
            <StatWidget
              label="Outstanding"
              value={statistics.partialOrders || 0}
              icon="las la-exclamation-circle"
              color="warning"
            />
          </Row>

          <Row className="mb-4">
            <StatWidget
              label="Incomplete"
              value={statistics.incompleteOrders || 0}
              icon="las la-times-circle"
              color="danger"
            />
            <StatWidget
              label="Current Bookings"
              value={currentBookings.length}
              icon="las la-calendar-check"
              color="info"
            />
            <StatWidget
              label="Upcoming Bookings"
              value={upcomingBookings.length}
              icon="las la-calendar-plus"
              color="primary"
            />
            <Col xl={3} md={6}>
              <Card className="card-animate overflow-hidden">
                <CardBody style={{ height: "100px" }} className="d-flex align-items-center">
                  <div className="flex-grow-1 overflow-hidden">
                    <p className="text-uppercase fw-semibold fs-14 text-muted mb-1">
                      Active Farm
                    </p>
                    <h6 className="mb-0 text-truncate fs-16" title={statistics.farmhouseName}>
                      {statistics.farmhouseName || "N/A"}
                    </h6>
                  </div>
                  <div className="avatar-sm flex-shrink-0 ms-2">
                    <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                      <i className="las la-building"></i>
                    </span>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Bookings Table / Calendar View */}
          <Row className="mb-0">
            <Col md={12}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-4">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
                    <div className="mb-3 mb-md-0">
                      <h5 className="card-title mb-1">Booking Calendar</h5>
                      <p className="text-muted mb-0">Overview of all bookings and their status</p>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <div className="d-flex align-items-center">
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill">Paid</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-3 py-2 rounded-pill">Outstanding</span>
                      </div>
                      <div className="d-flex align-items-center">
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 rounded-pill">Unpaid/Cancel</span>
                      </div>
                    </div>
                  </div>

                  <div className="calendar-container">
                    <FullCalendar
                      plugins={[dayGridPlugin, interactionPlugin, bootstrapPlugin]}
                      initialView="dayGridMonth"
                      themeSystem="bootstrap"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth",
                      }}
                      events={calendarEvents}
                      eventContent={renderEventContent}
                      editable={false}
                      droppable={false}
                      selectable={true}
                      eventClick={handleEventClick}
                      height="auto"
                      dayMaxEvents={3}
                      eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short'
                      }}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Calendar Event Details Modal */}
          <Modal isOpen={eventModal} toggle={toggleEventModal} centered size="md" className="event-modal">
            <ModalHeader toggle={toggleEventModal} className="bg-light p-3 border-bottom-0">
              Booking Details
            </ModalHeader>
            <ModalBody className="p-4">
              {selectedEvent && (
                <div>
                  <div className="d-flex align-items-start justify-content-between mb-4">
                    <div className="d-flex align-items-center">
                      <div className="avatar-md bg-light rounded-circle d-flex align-items-center justify-content-center me-3">
                        <span className="fs-1 fw-bold text-primary">{selectedEvent.customerName?.charAt(0) || 'G'}</span>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{selectedEvent.customerName || 'Guest'}</h5>
                        <p className="text-muted mb-0"><i className="las la-phone me-1"></i>{selectedEvent.userInfo?.mobile || selectedEvent.user?.mobile || 'N/A'}</p>
                      </div>
                    </div>
                    <Badge
                      color={
                        selectedEvent.paymentStatus === 'paid' ? 'success' :
                          selectedEvent.paymentStatus === 'partial' ? 'warning' :
                            'danger'
                      }
                      className="fs-12 px-3 py-2 rounded-pill"
                    >
                      {selectedEvent.paymentStatus?.toUpperCase()}
                    </Badge>
                  </div>

                  <div className="bg-light rounded p-3 mb-4">
                    <Row className="g-3">
                      <Col xs={6}>
                        <p className="text-muted text-uppercase fs-11 fw-semibold mb-1">Check-In</p>
                        <h6 className="fw-bold mb-0">{getCheckInDisplay(selectedEvent)}</h6>
                      </Col>
                      <Col xs={6}>
                        <p className="text-muted text-uppercase fs-11 fw-semibold mb-1">Check-Out</p>
                        <h6 className="fw-bold mb-0">{getCheckOutDisplay(selectedEvent)}</h6>
                      </Col>
                    </Row>
                  </div>

                  <Row className="g-3">
                    <Col sm={6}>
                      <div className="p-2 border rounded">
                        <p className="text-muted text-uppercase fs-11 fw-semibold mb-1">Total Amount</p>
                        <h5 className="text-primary mb-0">₹{parseFloat(selectedEvent.finalTotal || selectedEvent.finalPrice || 0).toLocaleString('en-IN')}</h5>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="p-2 border rounded">
                        <p className="text-muted text-uppercase fs-11 fw-semibold mb-1">Guests</p>
                        <h5 className="text-dark mb-0">{selectedEvent.numberOfPersons || 0}</h5>
                      </div>
                    </Col>

                    <Col sm={12}>
                      {(selectedEvent.remainingAmount <= 0 || selectedEvent.paymentStatus === 'paid') ? (
                        <div className="alert alert-success mb-0 d-flex align-items-center">
                          <i className="las la-check-circle fs-20 me-2"></i>
                          <div>
                            <h6 className="mb-0 text-success">Fully Paid</h6>
                          </div>
                        </div>
                      ) : (
                        <div className="alert alert-warning mb-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <span>Paid Amount:</span>
                            <span className="fw-bold fs-14">₹{selectedEvent.paidAmount?.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-2 border-top border-warning-subtle pt-2">
                            <span className="text-danger">Remaining:</span>
                            <span className="fw-bold text-danger fs-14">₹{selectedEvent.remainingAmount?.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      )}
                    </Col>
                  </Row>

                  <div className="mt-4 text-end">
                    <Button color="light" onClick={toggleEventModal} className="w-100">Close Details</Button>
                  </div>
                </div>
              )}
            </ModalBody>
          </Modal>

          {/* Custom Styles for Calendar */}
          <style>
            {`
               /* Calendar Modernization */
               .fc-theme-bootstrap a:not([href]) {
                    color: inherit;
               }
               .fc-col-header-cell-cushion {
                    color: #878a99;
                    font-weight: 600;
                    text-transform: uppercase;
                    font-size: 0.75rem;
                    padding-top: 15px;
                    padding-bottom: 15px;
               }
               .fc .fc-toolbar-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #495057;
               }
               .fc-daygrid-day-number {
                    font-size: 0.85rem;
                    font-weight: 600;
                    margin: 8px;
                    width: 30px;
                    height: 30px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    color: #495057;
               }
               /* Past dates: faded/blurred look so current & future stand out */
               .fc-daygrid-day.fc-day-past {
                    opacity: 0.5;
               }
               .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
                    background-color: #0ab39c; /* Primary Color */
                    color: #fff;
               }
               .fc-daygrid-day.fc-day-today {
                    background-color: rgba(10, 179, 156, 0.05) !important;
               }
               .fc-theme-bootstrap .fc-scrollgrid {
                    border: none;
               }
               .fc-theme-bootstrap td, .fc-theme-bootstrap th {
                    border: 1px solid #f3f3f9;
               }
               .fc-button-primary {
                    background-color: white !important;
                    border-color: #e9ecef !important;
                    color: #495057 !important;
                    box-shadow: none !important;
                    text-transform: capitalize;
                    font-weight: 500;
                    padding: 0.4rem 1rem !important;
                    border-radius: 0.25rem !important;
               }
               .fc-button-primary:hover {
                    background-color: #f3f6f9 !important;
                    color: #495057 !important;
               }
               .fc-button-primary:not(:disabled).fc-button-active {
                    background-color: #0ab39c !important;
                    border-color: #0ab39c !important;
                    color: white !important;
               }
               .fc-button-group {
                   box-shadow: 0 1px 2px rgba(0,0,0,0.05);
                   border-radius: 4px;
               }
               .calendar-container .fc-daygrid-event {
                   border: none !important;
                   background: transparent !important;
                   margin: 2px 4px !important;
               }
               .fc-daygrid-more-link {
                   color: #0ab39c !important;
                   font-weight: 600;
               }
               @media (max-width: 767.98px) {
                  .calendar-container .fc .fc-toolbar {
                    display: flex;
                    flex-wrap: wrap;
                    justify-content: center;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                  }
                  .calendar-container .fc .fc-toolbar-chunk {
                    display: flex;
                    justify-content: center;
                    width: 100%;
                  }
                  .calendar-container .fc .fc-toolbar-title {
                    font-size: 1.35rem;
                    text-align: center;
                    margin: 0;
                  }
                  .calendar-container .fc .fc-prev-button,
                  .calendar-container .fc .fc-next-button {
                    min-width: 44px;
                  }
                  .calendar-container .fc .fc-dayGridMonth-button {
                    min-width: 90px;
                  }
               }
            `}
          </style>
        </>
      )}

      {!statistics && !selectedFarm && !farmStatistics.loading && (
        <div className="text-center py-5">
          <div className="avatar-lg mx-auto mb-4">
            <div className="avatar-title bg-light text-primary rounded-circle display-5">
              <i className="las la-hand-pointer"></i>
            </div>
          </div>
          <h5 className="text-primary">No Farm Selected</h5>
          <p className="text-muted">Please select a farmhouse from the dropdown above to view its booking calendar and statistics.</p>
        </div>
      )}
    </div>
  );
};

export default Statement;

